import { useEffect, useRef, useState } from 'react';
import { ACCOUNT_TYPES, CHILEAN_BANKS } from '@/lib/chileanBanks.js';
import {
  chileYmd,
  isValidDateInput,
  MAX_DATE_INPUT,
  toISODate,
} from '@/lib/dateUtils.js';
import { formatRutInput, validateRutInput } from '@/lib/rut.js';
import Input from '@/ui/Form/Input.jsx';
import Button from '@/ui/Button/Button.jsx';
import {
  BREAKDOWN_STALE_ERRORS,
  breakdownItems,
  breakdownRows,
} from '@/lib/priceBreakdown.js';
import PriceBreakdownFields from '../solicitudes/PriceBreakdownFields.jsx';

const fieldErrors = {
  DateOfferedAfterDeadline: [
    'dateOffered',
    'La fecha debe estar dentro del plazo de la solicitud.',
  ],
  DateOfferedBeforeToday: [
    'dateOffered',
    'La fecha no puede ser anterior a hoy en Chile.',
  ],
  PriceRequired: ['price', 'Indica un precio.'],
  InvalidAmount: [
    'price',
    'Indica un precio válido en pesos, entero y mayor que cero.',
  ],
  TermsNotAccepted: [
    'termsAccepted',
    'Debes aceptar los términos para enviar tu oferta.',
  ],
};
const registryErrors = [
  'RegistryNoMatch',
  'RegistryCourtUnresolved',
  'MultipleRegistryMatches',
];
const accountErrors = ['EmailAlreadyRegistered', 'ReceptorAlreadyOnboarded'];
// Link text when the API has no current terms title.
const TERMS_FALLBACK = 'los términos y condiciones';

export default function OfferForm({ quote, onSubmit, initialErrors = {} }) {
  const name = quote.receptor.name?.trim() ?? '';
  const space = name.indexOf(' ');
  const [form, setForm] = useState({
    price: '',
    dateOffered: '',
    firstName: space < 0 ? name : name.slice(0, space),
    lastName: space < 0 ? '' : name.slice(space + 1),
    rut: '',
    termsAccepted: false,
    accountBankName: '',
    accountHolderName: '',
    accountHolderRut: '',
    accountNumber: '',
    accountType: '',
  });
  const [errors, setErrors] = useState(initialErrors);
  // A request that names its recipients is priced per recipient (REC-1983).
  const rows = breakdownRows(quote.request.recipients);
  const [breakdown, setBreakdown] = useState({});
  const [breakdownErrors, setBreakdownErrors] = useState({});
  const [message, setMessage] = useState('');
  const [login, setLogin] = useState(false);
  const [forceAccount, setForceAccount] = useState(false);
  const [sending, setSending] = useState(false);
  const busy = useRef(false);
  const formElement = useRef(null);
  const generalAlert = useRef(null);
  const focusError = useRef(false);
  useEffect(() => {
    if (!focusError.current || sending) return;
    const target =
      formElement.current?.querySelector('[aria-invalid="true"]') ||
      generalAlert.current;
    target?.focus();
    focusError.current = false;
  }, [errors, message, sending]);
  const needsAccount = forceAccount || quote.receptor.payoutAccount === null;
  const today = chileYmd(new Date());
  const maximum = quote.request.taskDeadline
    ? toISODate(quote.request.taskDeadline)
    : MAX_DATE_INPUT;
  const change = (key, value) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({ ...previous, [key]: undefined }));
  };
  const field = (key, label, props = {}, help) => (
    <div className="sold-control">
      <label htmlFor={`offer-${key}`}>{label}</label>
      <Input
        id={`offer-${key}`}
        value={form[key]}
        onChange={(e) => change(key, e.target.value)}
        autoComplete="off"
        disabled={sending}
        aria-invalid={!!errors[key]}
        aria-describedby={
          [
            help ? `offer-${key}-help` : '',
            errors[key] ? `offer-${key}-error` : '',
          ]
            .filter(Boolean)
            .join(' ') || undefined
        }
        {...props}
      />
      {help && (
        <p className="oferta-reopen" id={`offer-${key}-help`}>
          {help}
        </p>
      )}
      {errors[key] && (
        <p role="alert" className="sold-form-error" id={`offer-${key}-error`}>
          {errors[key]}
        </p>
      )}
    </div>
  );
  const select = (key, label, choices) => (
    <div className="sold-control">
      <label htmlFor={`offer-${key}`}>{label}</label>
      <select
        id={`offer-${key}`}
        value={form[key]}
        disabled={sending}
        onChange={(e) => change(key, e.target.value)}
        aria-invalid={!!errors[key]}
        aria-describedby={errors[key] ? `offer-${key}-error` : undefined}
      >
        <option value="">Selecciona una opción</option>
        {choices.map(({ value, label: text }) => (
          <option key={value} value={value}>
            {text}
          </option>
        ))}
      </select>
      {errors[key] && (
        <p role="alert" className="sold-form-error" id={`offer-${key}-error`}>
          {errors[key]}
        </p>
      )}
    </div>
  );
  async function submit(event) {
    event.preventDefault();
    if (busy.current) return;
    const next = {};
    // Solo dígitos, o miles agrupados de a tres con un mismo separador
    // ("45.000", "45 000"). Cualquier otra forma se rechaza: no se adivina.
    const typed = form.price.trim().replace(/^\$\s*/, '');
    const grouped = /^\d+$|^\d{1,3}(\.\d{3})+$|^\d{1,3}( \d{3})+$/.test(typed);
    const priced = rows.length ? breakdownItems(rows, breakdown) : null;
    const price = priced
      ? String(priced.total ?? '')
      : grouped
        ? typed.replace(/[.\s]/g, '')
        : '';
    if (priced) {
      setBreakdownErrors(priced.errors);
      if (Object.keys(priced.errors).length) next.breakdown = true;
    } else if (
      !/^\d+$/.test(price) ||
      !Number.isSafeInteger(Number(price)) ||
      Number(price) <= 0
    )
      next.price = 'Indica un precio válido en pesos, entero y mayor que cero.';
    if (!isValidDateInput(form.dateOffered))
      next.dateOffered = 'Indica una fecha válida.';
    else if (form.dateOffered < chileYmd(new Date()))
      next.dateOffered = fieldErrors.DateOfferedBeforeToday[1];
    else if (maximum && form.dateOffered > maximum)
      next.dateOffered = fieldErrors.DateOfferedAfterDeadline[1];
    if (quote.terms.required) {
      if (!form.firstName.trim()) next.firstName = 'Indica tu nombre.';
      if (!form.lastName.trim()) next.lastName = 'Indica tu apellido.';
      const rutError = validateRutInput(form.rut.trim());
      if (rutError) next.rut = rutError;
      if (!form.termsAccepted)
        next.termsAccepted = fieldErrors.TermsNotAccepted[1];
    }
    // Se aceptan puntos, guiones y espacios al pegar el número; una letra se
    // rechaza en vez de borrarse.
    const accountNumber = /^[\d.\-\s]+$/.test(form.accountNumber)
      ? form.accountNumber.replace(/\D/g, '')
      : '';
    if (needsAccount) {
      for (const key of ['accountBankName', 'accountHolderName', 'accountType'])
        if (!form[key].trim()) next[key] = 'Completa este campo.';
      const rutError = validateRutInput(form.accountHolderRut.trim());
      if (rutError) next.accountHolderRut = rutError;
      if (!/^\d{4,20}$/.test(accountNumber))
        next.accountNumber = 'Debe tener entre 4 y 20 dígitos.';
    }
    setErrors(next);
    setMessage('');
    setLogin(false);
    if (Object.keys(next).length) {
      focusError.current = true;
      return;
    }
    const payload = {
      quoteId: quote.quoteId,
      price: Number(price),
      dateOffered: form.dateOffered,
      ...(priced ? { items: priced.items } : {}),
    };
    if (quote.terms.required)
      Object.assign(payload, {
        termsAccepted: true,
        // The API sends null when no terms version is current, and the
        // schema takes a string or nothing.
        ...(typeof quote.terms.version === 'string' && quote.terms.version
          ? { tycVersion: quote.terms.version }
          : {}),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        rut: formatRutInput(form.rut.trim()),
      });
    if (needsAccount)
      Object.assign(payload, {
        accountBankName: form.accountBankName,
        accountHolderName: form.accountHolderName.trim(),
        accountHolderRut: formatRutInput(form.accountHolderRut.trim()),
        accountNumber,
        accountType: form.accountType,
      });
    busy.current = true;
    setSending(true);
    try {
      await onSubmit(payload);
    } catch (error) {
      focusError.current = true;
      const entry = fieldErrors[error.name];
      if (BREAKDOWN_STALE_ERRORS.includes(error.name))
        setMessage(
          'Los destinatarios de la solicitud cambiaron. Abre de nuevo el link del correo para ver la solicitud actual.',
        );
      else if (priced && error.name === 'InvalidAmount')
        setErrors({
          breakdownTotal: 'El total supera el monto máximo permitido.',
        });
      else if (entry) setErrors({ [entry[0]]: entry[1] });
      else if (error.name === 'PaymentDataRequired') {
        setForceAccount(true);
        setMessage('Completa la cuenta donde recibirás el pago.');
      } else if (error.name === 'TermsVersionNotFound')
        setMessage(
          'Los términos cambiaron. Recarga el correo y vuelve a abrir el link.',
        );
      else if (registryErrors.includes(error.name))
        setMessage(
          'No pudimos confirmar tus datos en el registro de receptores. Revisa nombre, apellido y RUT.',
        );
      else if (accountErrors.includes(error.name)) {
        setMessage('Ya tienes una cuenta en Recepthor.');
        setLogin(true);
      } else
        setMessage(
          error.status === 429
            ? 'Demasiados intentos. Prueba en unos minutos.'
            : 'No pudimos enviar la oferta. Inténtalo de nuevo.',
        );
    } finally {
      busy.current = false;
      setSending(false);
    }
  }
  return (
    <form
      ref={formElement}
      className="sold-form oferta-form"
      onSubmit={submit}
      noValidate
      autoComplete="off"
    >
      {rows.length > 0 && (
        <PriceBreakdownFields
          idPrefix="offer-breakdown"
          rows={rows}
          values={breakdown}
          errors={breakdownErrors}
          totalError={errors.breakdownTotal}
          disabled={sending}
          onFormat={(index, key, value) =>
            setBreakdown((current) => ({
              ...current,
              [index]: { ...current[index], [key]: value },
            }))
          }
          onChange={(index, key, value) => {
            setBreakdown((current) => ({
              ...current,
              [index]: { ...current[index], [key]: value },
            }));
            setBreakdownErrors((current) =>
              current[index]?.[key]
                ? {
                    ...current,
                    [index]: { ...current[index], [key]: undefined },
                  }
                : current,
            );
            setErrors((current) => ({ ...current, breakdownTotal: undefined }));
          }}
        />
      )}
      <div className="oferta-form-grid">
        {rows.length === 0 &&
          field(
            'price',
            'Precio en pesos',
            {
              inputMode: 'numeric',
              placeholder: '45000',
            },
            'En pesos, sin decimales.',
          )}
        {field('dateOffered', 'Fecha ofrecida', {
          type: 'date',
          min: today,
          max: maximum,
        })}
      </div>
      {quote.terms.required && (
        <fieldset disabled={sending}>
          <legend>Tus datos</legend>
          <div className="oferta-form-grid">
            {field('firstName', 'Nombre')}
            {field('lastName', 'Apellido')}
            {field('rut', 'RUT del receptor')}
          </div>
          <label className="oferta-terms">
            <input
              type="checkbox"
              checked={form.termsAccepted}
              onChange={(e) => change('termsAccepted', e.target.checked)}
              aria-invalid={!!errors.termsAccepted}
              aria-describedby={
                errors.termsAccepted ? 'offer-terms-error' : undefined
              }
            />
            <span>
              Acepto{' '}
              {quote.terms.pdfUrl ? (
                <a
                  href={quote.terms.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {quote.terms.title || TERMS_FALLBACK}
                </a>
              ) : (
                quote.terms.title || TERMS_FALLBACK
              )}
            </span>
          </label>
          {errors.termsAccepted && (
            <p role="alert" className="sold-form-error" id="offer-terms-error">
              {errors.termsAccepted}
            </p>
          )}
        </fieldset>
      )}
      {needsAccount && (
        <fieldset disabled={sending}>
          <legend>Cuenta para recibir el pago</legend>
          <p>Te pagaremos a esta cuenta. Queda guardada en tu perfil.</p>
          <div className="oferta-form-grid">
            {select(
              'accountBankName',
              'Banco',
              CHILEAN_BANKS.map((bank) => ({
                value: bank.code,
                label: bank.name,
              })),
            )}
            {select('accountType', 'Tipo de cuenta', ACCOUNT_TYPES)}
            {field('accountHolderName', 'Titular')}
            {field('accountHolderRut', 'RUT del titular')}
            {field('accountNumber', 'Número de cuenta', {
              inputMode: 'numeric',
            })}
          </div>
        </fieldset>
      )}
      {message && (
        <p
          ref={generalAlert}
          role="alert"
          tabIndex={-1}
          className="sold-form-error"
        >
          {message}{' '}
          {login && <a href="/login?portal=receptor">Entrar a Recepthor</a>}
        </p>
      )}
      <Button
        type="submit"
        variant="seal"
        size="lg"
        className="oferta-submit"
        disabled={sending}
      >
        {sending ? 'Enviando…' : 'Enviar oferta'}
      </Button>
    </form>
  );
}
